import React, { useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { SideMenuLayout, SeverityDots } from "./ComparisonExplorer";
import { BORDER, SEVERITIES, TEXT_PRIMARY, TEXT_SECONDARY } from "../constants";

const CHECK_LABELS = {
  duplicateWords: "כפל מילים",
  footnotes: "כוכביות והערות שוליים",
  numbers: "שינויים בנתונים מספריים",
  parallelCorrections: "תיקון שבוצע רק בחלק מהאריזה",
  claimConsistency: "עקביות טענות בין אזורי האריזה",
  inciFormat: "תקינות רשימת INCI",
  fixedFields: "שדות קבועים וברקוד",
  fontSize: "גודל כתב",
  outlinedText: "טקסט שהומר לעקומות",
  mandatoryItems: "פריטי חובה",
};
const SEVERITY_RANK = { critical: 0, medium: 1, low: 2 };
const PASSED = "__passed";

const severityOf = (key) => SEVERITIES.find((s) => s.key === key);

const SeverityCounts = ({ issues }) => (
  <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
    {SEVERITIES.map(({ key, label, color }) => {
      const count = issues.filter((i) => i.severity === key).length;
      if (!count) return null;
      return (
        <Box key={key} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13, fontWeight: 700, color }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
          {count} {label}
        </Box>
      );
    })}
  </Box>
);

const IssueItem = ({ issue }) => (
  <Box sx={{ py: 1.5, "&:not(:last-of-type)": { borderBottom: `1px solid ${BORDER}` } }}>
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: severityOf(issue.severity)?.color, flexShrink: 0, mt: "6px" }} />
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, flex: 1, minWidth: 0 }}>{issue.message}</Typography>
      {issue.sectionLabel && (
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, flexShrink: 0 }}>{issue.sectionLabel}</Typography>
      )}
    </Box>
    {issue.excerpt?.match && (
      <Typography sx={{ fontSize: 15, color: TEXT_SECONDARY, mt: 0.75, lineHeight: 1.9, paddingInlineStart: "16px" }}>
        …{issue.excerpt.before}{" "}
        <Box component="span" sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700, px: "3px", borderRadius: "3px" }}>
          <bdi>{issue.excerpt.match}</bdi>
        </Box>{" "}
        {issue.excerpt.after}…
      </Typography>
    )}
  </Box>
);

const DetailCard = ({ title, badge, children }) => (
  <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.5,
        bgcolor: "#fafbfc",
        borderBottom: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: TEXT_PRIMARY }}>
        {title}
      </Typography>
      {badge}
    </Box>
    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 0.5 }}>{children}</Box>
  </Paper>
);

/**
 * Results of the automatic checks on the updated version (spec 3.5), laid
 * out like the comparison: the checks in a side menu and the selected
 * check's findings shown large. Checks that passed share one menu entry.
 */
const AutoChecksPanel = ({ autoChecks }) => {
  const groups = autoChecks.performed
    .map((check) => ({ check, issues: autoChecks.issues.filter((issue) => issue.check === check) }))
    .filter((group) => group.issues.length)
    .sort(
      (p, q) =>
        Math.min(...p.issues.map((i) => SEVERITY_RANK[i.severity])) -
        Math.min(...q.issues.map((i) => SEVERITY_RANK[i.severity])),
    );
  const passed = autoChecks.performed.filter((check) => !autoChecks.issues.some((issue) => issue.check === check));
  const [selectedKey, setSelectedKey] = useState(null);
  const selected = selectedKey || groups[0]?.check || PASSED;
  const group = groups.find((g) => g.check === selected);

  const items = [
    ...groups.map((g) => ({ key: g.check, label: CHECK_LABELS[g.check] || g.check, badge: <SeverityDots items={g.issues} /> })),
    ...(passed.length
      ? [
          {
            key: PASSED,
            label: "בדיקות שעברו",
            badge: (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, fontSize: 13, fontWeight: 700, color: "#15803d" }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                {passed.length}
              </Box>
            ),
          },
        ]
      : []),
  ];

  return (
    <SideMenuLayout
      title={groups.length ? `${groups.length} בדיקות עם ממצאים · ${passed.length} עברו` : `כל ${passed.length} הבדיקות עברו`}
      items={items}
      selectedKey={selected}
      onSelect={setSelectedKey}
    >
      {group ? (
        <DetailCard title={CHECK_LABELS[group.check] || group.check} badge={<SeverityCounts issues={group.issues} />}>
          {group.issues.map((issue) => (
            <IssueItem key={issue.id} issue={issue} />
          ))}
          {group.check === "mandatoryItems" && autoChecks.checklist?.length > 0 && (
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, py: 1 }}>
              נמצאו {autoChecks.checklist.filter((item) => item.found).length} מתוך {autoChecks.checklist.length} פריטי חובה
            </Typography>
          )}
        </DetailCard>
      ) : (
        <DetailCard
          title="בדיקות שעברו ללא ממצאים"
          badge={<Typography sx={{ fontSize: 13, fontWeight: 700, color: "#15803d" }}>{passed.length}</Typography>}
        >
          {passed.map((check) => (
            <Box
              key={check}
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.25, "&:not(:last-of-type)": { borderBottom: `1px solid ${BORDER}` } }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#22c55e" }} />
              <Typography sx={{ fontSize: 15, color: TEXT_PRIMARY }}>{CHECK_LABELS[check] || check}</Typography>
            </Box>
          ))}
        </DetailCard>
      )}
    </SideMenuLayout>
  );
};

export default AutoChecksPanel;
